import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NfcCard, NfcCardList, NfcUid } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Button, Card, EmptyState, Spinner, TextField } from '../../components/ui';
import { NFC } from './api';
import { CARDS_KEY, CardActions, CardStatus, hintText } from './nfcCommon';

/** True when what was typed or scanned is a plausible card number (hex, separators allowed). */
export function validUid(text: string): boolean {
  return NfcUid.safeParse(text).success;
}

/**
 * Member drawer tab "Cards" (contract nfc-cards s4): list, add by typing or scanning with a USB reader (it types the
 * number and presses Enter), block with a reason, unblock, retire. Exported for W23 to mount in the member drawer.
 */
export function MemberCardsTab({ memberId }: { memberId: string }) {
  const canManage = useCan('nfc_cards.manage');
  const qc = useQueryClient();
  const toast = useToast();
  const uidBox = useRef<HTMLDivElement>(null);
  const [uid, setUid] = useState('');
  const [label, setLabel] = useState('');
  const [localError, setLocalError] = useState('');
  const q = useQuery({
    queryKey: [...CARDS_KEY, 'member', memberId],
    queryFn: () =>
      api.get(NFC, {
        query: { memberId, status: 'all', pageSize: 50 },
        schema: NfcCardList,
      }),
  });
  const add = useMutation({
    mutationFn: () => api.post(NFC, { body: { memberId, uid, label }, schema: NfcCard }),
    onSuccess: async () => {
      toast(t('w18.nfc.added'));
      setUid('');
      setLabel('');
      await qc.invalidateQueries({ queryKey: CARDS_KEY });
      uidBox.current?.querySelector('input')?.focus();
    },
  });
  const errors = fieldErrors(add.error);
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Spinner />;
  return (
    <div className="space-y-5">
      {canManage ? (
        <Card title={t('w18.nfc.addTitle')} description={t('w18.nfc.addHelp')}>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              setLocalError('');
              if (!validUid(uid)) return setLocalError(t('w18.nfc.badUid'));
              add.mutate();
            }}
          >
            <div ref={uidBox}>
              <TextField
                label={t('w18.nfc.uid')}
                autoComplete="off"
                inputMode="text"
                placeholder={t('w18.nfc.uidPlaceholder')}
                value={uid}
                error={localError || errors['uid']}
                onChange={(e) => setUid(e.target.value)}
              />
            </div>
            <TextField label={t('w18.nfc.label')} maxLength={60} value={label} onChange={(e) => setLabel(e.target.value)} />
            <ErrorLine error={add.error} />
            <Button type="submit" variant="primary" disabled={add.isPending || uid.trim() === ''}>
              {t('w18.nfc.addButton')}
            </Button>
          </form>
        </Card>
      ) : null}
      {q.data.items.length === 0 ? (
        <EmptyState message={t('w18.nfc.noneForMember')} />
      ) : (
        <ul className="space-y-3">
          {q.data.items.map((c) => (
            <li key={c.id} className="rounded-lg border border-slate-200 p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-slate-900">
                  {c.label || t('w18.nfc.unnamed')} <span className="font-mono text-xs text-slate-600">{hintText(c.hint)}</span>
                </span>
                <CardStatus card={c} />
              </div>
              <p className="mt-1 text-xs text-slate-600">
                {t('w18.nfc.registeredVia', {
                  via: t(`w18.nfc.via.${c.registeredVia}`),
                  date: formatDateTime(c.createdAt),
                })}
                {' · '}
                {c.lastUsedAt
                  ? t('w18.nfc.lastUsed', {
                      date: formatDateTime(c.lastUsedAt),
                    })
                  : t('w18.nfc.neverUsed')}
              </p>
              <div className="mt-2">
                <CardActions card={c} canManage={canManage} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
